import React from 'react';
import { useSelector } from 'react-redux';
import Immutable from 'immutable';

import SelectWorkspaceRow from 'js/src/components/common/select-workspace-row';
import { BUTTON_COLORS, BUTTON_SIZES } from 'js/src/components/common/ui/Button';
import type { DropdownItemProp } from 'js/src/components/common/ui/Dropdown';
import { Dropdown } from 'js/src/components/common/ui/Dropdown';
import { selectActiveWorkspaceName, selectAllWorkspaceOrmIds } from 'js/src/data/account';
import { ImmutableFromJS } from 'js/src/libs/consts';
import Cookies from 'js-cookie';

type TogglePropsType = Immutable.Collection<string, string>;
type ItemPropsType = DropdownItemProp<object>;
type ItemPropsListType = Immutable.List<ItemPropsType>;

const calculateItemPropsList = (workspaceIds: Immutable.List<number>): ItemPropsListType => {
    const items: ItemPropsType[] = [];
    workspaceIds.forEach((id) => {
        const itemProps = ImmutableFromJS({
            id: id.toString(),
            workspaceIdField: id,
            itemComponent: SelectWorkspaceRow,
        }) as unknown as ItemPropsType;
        items.push(itemProps);
    });
    return Immutable.List([...items]);
};

const calculateToggleProps = (activeWorkspaceName: string) => {
    return Immutable.fromJS({
        text: activeWorkspaceName,
        iconRight: 'expand_more',
        color: BUTTON_COLORS.SIMPLE,
        size: BUTTON_SIZES.MD,
        className: 'workspace-selector-toggle',
    });
};

const showWorkspaceSelectorLoading = Cookies.get('show-workspace-selector-loading') === 'true';

const WorkspaceSelector = React.memo(function WorkspaceSelector() {
    const [toggleProps, setToggleProps] = React.useState<TogglePropsType | null>(null);
    const [itemPropsList, setItemPropsList] = React.useState<ItemPropsListType | null>(null);
    const workspaceIds = useSelector(selectAllWorkspaceOrmIds);
    const activeWorkspaceName = useSelector(selectActiveWorkspaceName);

    React.useEffect(() => {
        setToggleProps(calculateToggleProps(activeWorkspaceName));
    }, [activeWorkspaceName]);

    React.useEffect(() => {
        setItemPropsList(calculateItemPropsList(workspaceIds));
    }, [workspaceIds]);

    // still loading if workspaceIds is empty or toggleProps/itemPropsList are not set (effect hasn't run)
    const hasLoaded =
        workspaceIds.size > 0 &&
        toggleProps !== null &&
        itemPropsList !== null &&
        activeWorkspaceName !== '';

    const shouldShow = workspaceIds.size > 1; // only show the dropdown if we have >1 workspace
    React.useEffect(() => {
        if (!hasLoaded) {
            return;
        }
        // if we should show, set a cookie so next time we'll show padding while loading
        Cookies.set('show-workspace-selector-loading', shouldShow ? 'true' : 'false');
    }, [hasLoaded, shouldShow]);

    if (!hasLoaded) {
        // if we haven't loaded workspaces yet, show a loading spacer if showWorkspaceSelectorLoading is true
        if (showWorkspaceSelectorLoading) {
            return <div style={{ height: '40px' }} />;
        } else {
            return null;
        }
    }

    if (!shouldShow) {
        return null;
    }

    return <Dropdown limitMenuHeight toggleProps={toggleProps} itemPropsList={itemPropsList} />;
});

export default WorkspaceSelector;
